import React from 'react';
import PropTypes from 'prop-types';
import ensureKeyed from '../../utils/ensureKeyed';
import FontAwesome from 'react-fontawesome';
import {default as Select, Creatable} from 'react-select';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import stringifyCriteria from '../../utils/stringifyCriteria';

const comparisonOperators = [
  '==', '!=',
  '>', '>=',
  '<', '<=',
  'include?',
  'starts_with?',
  'ends_with?'
];

const unaryOperators = [
  'present?',
  'blank?'
];

const conjunctionOperators = [
  '&&', '||'
];

const stringTypes = [
  'string',
  'IdField',
  'NameField',
  'NameTitleField',
  'TextField',
  'NameSuffixField',
  'AddressField',
  'CountryField',
  'StateField',
  'PostalField',
  'ApplyGradeField',
  'SingleSelectField',
  'CurrentGradeField',
  'PhoneField',
  'EmailField',
  'RelationshipField',
  'SignatureField',
  'ApplyYearField'
];

const stringOperators = [
  '==', '!=', 'include?', 'starts_with?', 'ends_with?', 'present?', 'blank?'
];

const dateTypes = [
  'date',
  'BirthField',
  'DateField',
];

const dateOperators = [
  '<=', '>=', 'present?', 'blank?'
];

const booleanTypes = [
  'boolean',
  'BooleanField'
];

const booleanOperators = [
  '==', '!=', 'present?', 'blank?'
];

const arrayTypes = [
  'array',
  'BooleanlistField'
];

const arrayOperators = [
  'include?', 'present?', 'blank?'
];

function mapFieldType(fieldSpec) {
  const fieldType = fieldSpec ? (fieldSpec.type || fieldSpec.ctype) : 'string';

  if(stringTypes.includes(fieldType)){
    return 'string';
  }
  else if (dateTypes.includes(fieldType)){
    return 'date';
  }
  else if (booleanTypes.includes(fieldType)){
    return 'boolean';
  }
  else if (arrayTypes.includes(fieldType)){
    return 'array';
  }

  return 'string';
}

function operatorsForFieldType(fieldSpec){
  switch(mapFieldType(fieldSpec)) {
    case 'string': return stringOperators;
    case 'date': return dateOperators;
    case 'boolean': return booleanOperators;
    case 'array': return arrayOperators;
    default: return comparisonOperators;
  }
}

function fieldByCanonicalName(fields, canonicalName){
  // TODO : fields already indexed in state...
  return canonicalName && fields.find((f) => f.name === canonicalName)
}

function operatorDisplayName(operator){
  switch(operator){
    case '==': return 'is';
    case '!=': return 'is not';
    case '&&': return 'AND';
    case '||': return 'OR';
    case 'include?': return 'contains';
    case 'starts_with?': return 'starts with';
    case 'ends_with?': return 'sends with';
    case 'present?': return 'is not blank';
    case 'blank?': return 'is blank';
    default: return operator;
  }
}

const comparisonType = PropTypes.shape({
  operator: PropTypes.oneOf(comparisonOperators),
  field: PropTypes.string,
  value: PropTypes.string,
  ignore_case: PropTypes.bool
});

const conjunctionType = PropTypes.shape({
  operator: PropTypes.oneOf(conjunctionOperators),
  criteria: PropTypes.arrayOf(PropTypes.object)
});

class RuleControl extends React.Component {
  static propTypes = {
    value: PropTypes.oneOfType([
      comparisonType,
      conjunctionType
    ]),
    availableFields: PropTypes.arrayOf(PropTypes.shape({
      canonical_name: PropTypes.string,
      display_name: PropTypes.string,
      type: PropTypes.string // TODO : Specify types?
    })),

    onChange: PropTypes.func
  };

  static defaultProps = {
    edit: false,
    onChange: (value)=>{},
    availableFields: []
  };

  render(){
    const { value } = this.props;
    const { onChange } = this.props;

    if (!value){
      return <a className='btn btn-secondary'
                onClick={()=>onChange({})}>
        Add Rules
      </a>;
    }

    const { operator = '==' } = value;

    return <div className='rule-control'>
      {(()=>{
        if (conjunctionOperators.includes(operator)) {
          return <ConjunctionRule {...this.props}/>;
        }

        return <ComparisonRule {...this.props}/>;
      })()}
    </div>;
  }
}

class ComparisonRule extends React.Component {
  static propTypes = {
    value: comparisonType,

    onChange: PropTypes.func
  };

  static defaultProps = {
    onChange: (value)=>{},
    depth: 0
  };

  constructor(props){
    super();

    const {value={}} = props;

    this.state = {
      editing: !value.operator || !value.field
    };
  }

  UNSAFE_componentWillReceiveProps(props){
    const { value={} } = this.props;
    this.setState({
      editing: this.state.editing || !value.operator || !value.field
    });
  }

  showEdit(){
    this.setState({
      editing: true
    })
  }

  hideEdit(){
    this.setState({
      editing: false
    })
  }

  render(){
    const { editing } = this.state;
    const { value } = this.props;
    const { depth } = this.props;
    const { availableFields } = this.props;

    if(editing){
      return <ComparisonEditor {...this.props} hideEdit={()=>this.hideEdit()}/>;
    }

    return <div className='btn-group' onClick={()=>this.showEdit()}>
      <a className={`btn btn-light rule-depth-${depth%4}`}>
        <span className='float-start'>
          {stringifyCriteria(value, field => fieldByCanonicalName(availableFields, field))}
        </span>
        <span className='btn btn-primary btn-xs float-end'>
          <FontAwesome name='pencil' />
        </span>
      </a>
    </div>;
  }
}

class ComparisonEditor extends React.Component {
  updateFieldName(field){
    const { availableFields } = this.props;

    const fieldSpec = field && fieldByCanonicalName(availableFields, field);
    const fieldType = mapFieldType(fieldSpec);

    return this.props.onChange(Object.assign({}, this.props.value, {
      field,
      field_type: fieldType,
      value: "",
      operator: ""
    }));
  }

  updateValue(value){
    return this.props.onChange(Object.assign({}, this.props.value, {value}));
  }

  handleValueChange(event){
    const value = event.target.value;
    return this.updateValue(value);
  }

  updateOperator(event){
    const operator = event.target.value;
    return this.props.onChange(Object.assign({}, this.props.value, {operator}));
  }

  createConjunction(operator){
    const {value} = this.props;
    const {onChange} = this.props;

    onChange({
      operator,
      criteria: [
        value,
        {}
      ]
    });
  }

  getAvailableFields(){
    const { availableFields=[] } = this.props;
    const { value={} } = this.props;

    let seenCurrent = false;

    const mappedFields = availableFields.map((field) => {
      seenCurrent = value.field && seenCurrent || (field.name == value.field);

      return {
        value: field.name,
        label: field.display_name
      }
    });

    const currentUnknownField = [
      !seenCurrent && value.field && { label: value.field, value: value.field }
    ];

    return mappedFields.concat(currentUnknownField).filter((x)=>x);
  }

  renderValueEditor(){
    const { value } = this.props;
    const { availableFields } = this.props;

    const fieldSpec = value.field && fieldByCanonicalName(availableFields, value.field);
    const fieldType = mapFieldType(fieldSpec);

    if (fieldType == 'date'){
      return <DatePicker selected={value.value && moment(value.value).toDate()}
                         onChange={(date) => this.updateValue(moment(date).format('YYYY-MM-DD'))}
                         className='form-control form-control-sm'
      />;
    }

    if (fieldSpec.options && fieldSpec.options.length > 0){
      return <Select value={value.value}
                        placeholder='Select an Option'
                        clearable={false}
                        options={fieldSpec.options.map(opt => {
                          return Array.isArray(opt)
                            ? {label: opt[0], value: opt[1]}
                            : {label: opt, value: opt}
                        })}
                        onChange={({value})=>this.updateValue(value)}
      />;
    }

    return <input type='text'
                  value={value.value}
                  className='form-control form-control-sm'
                  placeholder='Field Value'
                  onChange={this.handleValueChange.bind(this)}/>;
  }

  render(){
    const { value } = this.props;
    const { onChange } = this.props;
    const { parentOperator } = this.props;
    const { availableFields } = this.props;

    const fieldSpec = value.field && fieldByCanonicalName(availableFields, value.field);
    const operatorsForField = operatorsForFieldType(fieldSpec);

    return <div className='card'>
      <div className='card-header bg-primary text-white text-center'>
        {stringifyCriteria(value, field => fieldByCanonicalName(availableFields, field))}
      </div>

      <div className='card-body'>
        <div className='row control-group'>
          <div className='col-6 text-end'>
            <label className='col-form-label'>Field</label>
          </div>
          <div className='col-18'>
            <Select value={value.field}
                       placeholder='Field Name'
                       clearable={false}
                       options={this.getAvailableFields()}
                       scrollIntoView
                       onChange={({value})=>this.updateFieldName(value)}
            />
          </div>
        </div>

        { value.field &&
        <div className='row control-group'>
          <div className='col-6 text-end'>
            <label className='col-form-label'>Operator</label>
          </div>
          <div className='col-18'>
            <select onChange={(event)=>this.updateOperator(event)}
                    value={value.operator}
                    className='form-select add-criteria-field-operator'>
              <option value={null} className='option-placeholder'>Select an Operator</option>
              {operatorsForField.map((oper)=><option value={oper}>
                {operatorDisplayName(oper)}
              </option>).map(ensureKeyed())}
            </select>
          </div>
        </div>
        }

        { value.field && value.operator && !unaryOperators.includes(value.operator) &&
        <div className='row control-group'>
          <div className='col-6 text-end'>
            <label className='col-form-label'>Value</label>
          </div>
          <div className='col-18'>
            {this.renderValueEditor()}
          </div>
        </div>
        }
      </div>

      <div className='card-footer'>
        <div className='btn-group' role='group'>
          <a className='btn btn-sm btn-danger'
             onClick={()=>onChange(null)}
             data-bs-toggle='tooltip'
             title='Remove this term'>
            <FontAwesome name='close'/>&nbsp; Remove
          </a>
          <a className='btn btn-sm btn-primary'
             onClick={()=>this.createConjunction('&&')}
             data-bs-toggle='tooltip'
             title={(parentOperator == '&&') ? 'Add an AND rule to this term' : 'Create an AND rule with this term'}>
            <FontAwesome name='plus'/> AND
          </a>
          <a className='btn btn-sm btn-primary'
             onClick={()=>this.createConjunction('||')}
             data-bs-toggle='tooltip'
             title={(parentOperator == '||') ? 'Add an OR rule to this term' : 'Create an OR rule with this term'}>
            <FontAwesome name='plus'/> OR
          </a>
          <a className='btn btn-sm btn-success' onClick={()=>this.props.hideEdit()}><FontAwesome name='pencil'/>&nbsp;
            Done</a>
        </div>
      </div>

    </div>;
  }
}

class ConjunctionRule extends React.Component {
  static propTypes = {
    value: conjunctionType,

    onChange: PropTypes.func
  };

  static defaultProps = {
    onChange: (value)=>{},
    depth: 0
  };


  addCriteria(){
    const { value } = this.props;

    this.onChange(Object.assign({}, value, {
      criteria: value.criteria.concat({})
    }))
  }

  changeCriterion(changedIndex, newValue){
    const { value } = this.props;
    const { criteria } = value;

    if(!newValue){
      return this.removeCriterion(changedIndex);
    }

    const newCriteria = criteria.map((crit,i) => (changedIndex == i) ? newValue : crit);

    this.onChange(Object.assign({}, value, {
      criteria: newCriteria
    }));
  }

  createConjunction(operator){
    const {value} = this.props;

    this.onChange({
      operator,
      criteria: [
        value,
        {}
      ]
    });
  }

  removeCriterion(index){
    const { value } = this.props;
    const { criteria=[] } = value;

    const newCriteria = criteria.filter((c, i) => (i != index));

    if(newCriteria.length > 1){
      this.onChange(Object.assign({}, value, {
        criteria: newCriteria
      }));
    }
    else if (newCriteria.length == 1){
      this.onChange(newCriteria[0]);
    }
    else {
      this.onChange(null);
    }
  }

  onChange(value){
    this.props.onChange(value && this.reduceCriteria(value));
  }

  reduceCriteria(value){
    const { operator } = value;
    const { criteria=[] } = value;

    let newCriteria = [];

    criteria && criteria.forEach((criterion) => {
      if (criterion.operator != operator){
        newCriteria = newCriteria.concat(criterion)
      }
      else{
        newCriteria = newCriteria.concat(criterion.criteria)
      }
    });

    return Object.assign({}, value, {
      criteria: newCriteria
    });
  }

  render(){
    const { value } = this.props;
    const { parentOperator } = this.props;
    const { operator } = value;
    const oppositeOperator = (operator == '&&') ? '||' : '&&';
    const depth = this.props.depth + 1;

    const { criteria = [] } = value;

    return <div>
      <div className={`card rule-depth-${depth%4}`}>
        <div className='card-body'>
          {criteria.map((crit, i) => <div>
              <RuleControl {...this.props}
                           value={crit}
                           onChange={(newValue)=>this.changeCriterion(i, newValue)}
                           parentOperator={operator}
                           depth={depth}
              />
              {(i < criteria.length-1) && <div className='conjunction-divider'>
                {operatorDisplayName(operator)}
              </div>}
            </div>
          ).map(ensureKeyed(`criterion_${depth}`))}

          <div className='conjunction-divider'>
            <a className='btn btn-sm btn-primary'
               data-bs-toggle='tooltip'
               title={`Add an ${operatorDisplayName(operator)} term to this rule`}
               onClick={()=>this.addCriteria()}>
              {operatorDisplayName(operator)}
            </a>
          </div>
        </div>
      </div>

      {(oppositeOperator != parentOperator) &&
      <div className='conjunction-divider'>
        <a className='btn btn-sm btn-primary'
           data-bs-toggle='tooltip'
           title={`Create an ${operatorDisplayName(oppositeOperator)} rule with this term`}
           onClick={()=>this.createConjunction(oppositeOperator)}>
          {operatorDisplayName(oppositeOperator)}
        </a>
      </div>
      }
    </div>;

  }
}

export default RuleControl;
export {stringifyCriteria};
